import { FunctionComponent, useEffect, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row } from "antd";
import dayjs from "dayjs";
import {
    LIBELLES_MODE_PAIEMENT,
    MODES_PAIEMENT_SAISISSABLES,
    ModePaiement,
    PaiementBack,
    PaiementFront,
} from "../../services/paiement";
import { APPLICATION_DATE_FORMAT } from "../../utils/FormUtils";
import { DatePickerFormItem } from "../common/DatePickerFormItem";
import { InputFormItem } from "../common/InputFormItem";
import { InputNumberFormItem } from "../common/InputNumberFormItem";
import { SelectFormItem } from "../common/SelectFormItem";
import { formatMontant } from "./paiementUtils";

export type ModalSaisiePaiementProps = {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    /** Renseigné en modification, absent en création. */
    paiement?: PaiementBack;
    /** Plafond opposable à la saisie : reste à payer, paiement en cours de modification réintégré. */
    montantMaximum: number;
    /** Retourne un message d'erreur à afficher, ou undefined si l'enregistrement a réussi. */
    onEnregistrer: (valeurs: PaiementFront) => Promise<string | undefined>;
};

/**
 * La saisie vit dans une modale et non en ligne : le bloc de paiements est monté à l'intérieur des
 * formulaires d'inscription, qui sont eux-mêmes des `<Form>` antd horizontaux. Un `<Form>` imbriqué
 * y produirait un `<form>` dans un `<form>` (HTML invalide) dont les `Form.Item` hériteraient en
 * cascade des règles `.ant-form-horizontal` du parent. Le portail d'une modale est monté au niveau
 * du `body` : il échappe à cette cascade, et la saisie est identique quel que soit l'appelant.
 */
export const ModalSaisiePaiement: FunctionComponent<ModalSaisiePaiementProps> = ({
    open,
    setOpen,
    paiement,
    montantMaximum,
    onEnregistrer,
}) => {
    const [form] = Form.useForm();
    const [erreur, setErreur] = useState<string | undefined>(undefined);
    const [enregistrement, setEnregistrement] = useState<boolean>(false);

    useEffect(() => {
        if (!open) {
            return;
        }
        setErreur(undefined);
        form.resetFields();
        form.setFieldsValue(paiement
            ? {
                montant: paiement.montant,
                datePaiement: dayjs(paiement.datePaiement, APPLICATION_DATE_FORMAT),
                mode: paiement.mode,
                reference: paiement.reference ?? undefined,
                commentaire: paiement.commentaire ?? undefined,
            }
            : { datePaiement: dayjs(), mode: ModePaiement.ESPECE });
    }, [open, paiement, form]);

    const close = () => {
        form.resetFields();
        setErreur(undefined);
        setOpen(false);
    };

    /**
     * Rejoue la règle du serveur pour un retour immédiat. Le serveur reste seul juge : il refait le
     * contrôle avec le montant réellement encaissé en base.
     */
    const validerMontant = (_: any, value: number) => {
        if (value === undefined || value === null) {
            return Promise.resolve();
        }
        if (value <= 0) {
            return Promise.reject("Le montant doit être strictement positif.");
        }
        if (value > montantMaximum) {
            return Promise.reject(`Le montant dépasse le reste à payer (${formatMontant(montantMaximum)}).`);
        }
        return Promise.resolve();
    };

    const soumettre = async (valeurs: PaiementFront) => {
        setEnregistrement(true);
        const messageErreur = await onEnregistrer(valeurs);
        setEnregistrement(false);
        if (messageErreur) {
            setErreur(messageErreur);
        } else {
            close();
        }
    };

    return (
        <Modal
            title={paiement ? "Modifier le paiement" : "Nouveau paiement"}
            open={open}
            width={640}
            onCancel={close}
            destroyOnClose
            footer={
                <>
                    <Button onClick={close} {...({ testid: "annuler-saisie-paiement" } as any)}>
                        Annuler
                    </Button>
                    <Button
                        type="primary"
                        loading={enregistrement}
                        onClick={() => form.submit()}
                        {...({ testid: "enregistrer-paiement" } as any)}
                    >
                        Enregistrer
                    </Button>
                </>
            }
        >
            <Form
                form={form}
                name="saisiePaiement"
                layout="vertical"
                autoComplete="off"
                onFinish={soumettre}
                className="paiement-form"
            >
                <Row gutter={24}>
                    <Col xs={24} sm={12}>
                        <InputNumberFormItem
                            name="montant"
                            label="Montant"
                            addonAfter="€"
                            min={0}
                            style={{ width: "100%" }}
                            rules={[
                                { required: true, message: "Veuillez saisir un montant" },
                                { validator: validerMontant },
                            ]}
                        />
                    </Col>
                    <Col xs={24} sm={12}>
                        <DatePickerFormItem
                            name="datePaiement"
                            label="Date du paiement"
                            rules={[
                                { required: true, message: "Veuillez saisir une date" },
                                {
                                    validator: (_: any, value: dayjs.Dayjs) =>
                                        value && value.isAfter(dayjs(), "day")
                                            ? Promise.reject("La date ne peut pas être dans le futur.")
                                            : Promise.resolve(),
                                },
                            ]}
                        />
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col xs={24} sm={12}>
                        <SelectFormItem
                            name="mode"
                            label="Mode de paiement"
                            options={MODES_PAIEMENT_SAISISSABLES.map((mode) => ({
                                label: LIBELLES_MODE_PAIEMENT[mode],
                                value: mode,
                            }))}
                            rules={[{ required: true, message: "Veuillez choisir un mode" }]}
                        />
                    </Col>
                    <Col xs={24} sm={12}>
                        <InputFormItem
                            name="reference"
                            label="Référence (n° de chèque)"
                            placeholder="Facultatif"
                        />
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col xs={24}>
                        <InputFormItem name="commentaire" label="Commentaire" placeholder="Facultatif" />
                    </Col>
                </Row>
                {erreur && <Alert type="error" showIcon message={erreur} />}
            </Form>
        </Modal>
    );
};
